// @flow
import * as React from 'react';
import {defineMessage} from 'react-intl';
import samplePaymentIntent from '../../responses/payment_intent';
import {BuildInstructions, DefaultCards} from '../../../../components/left-col';

import type {StepsConfig} from '../../../../data/GuideTypes';

const buildInstructions = [
  {
    label: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.ios.objc.steps.load-stripe.cocoa.label',
        defaultMessage: 'CocoaPods',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.ios.objc.steps.load-stripe.cocoa.body',
        defaultMessage:
          'Add this line to your Podfile, and use the .xcworkspace file to open your project in Xcode, instead of the .xcodeproj file, from here on out.',
      }),
    },
    command: `pod 'Stripe'`,
  },
  {
    label: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.ios.objc.steps.load-stripe.carthage.label',
        defaultMessage: 'Carthage',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.ios.objc.steps.load-stripe.carthage.body',
        defaultMessage: 'Add this line to your Cartfile.',
      }),
    },
    command: `github "stripe/stripe-ios"`,
  },
  {
    label: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.ios.objc.steps.load-stripe.dynamic.label',
        defaultMessage: 'Dynamic framework',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.ios.objc.steps.load-stripe.dynamic.body',
        defaultMessage:
          'To include Stripe in your project, download and unzip Stripe.xcframework.zip from a release on GitHub. Drag the required xcframework files to the "Embedded Binaries" settings in your Xcode project. Make sure to select "Copy items if needed". See the GitHub README for the list of required xcframeworks.',
      }),
    },
  },
];

export default ([
  {
    file: 'server',
    id: 'setup',
    section: 'setup-stripe',
  },
  {
    file: 'server',
    id: 'create',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.ios.objc.steps.create.heading',
        defaultMessage: 'Create a PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.ios.objc.steps.create.body',
        defaultMessage: `Add an endpoint on your server that creates a <a>PaymentIntent</a>. A PaymentIntent tracks the customer's payment lifecycle, keeping track of any failed payment attempts and ensuring the customer is only charged once. Return the PaymentIntent's client secret in the response to finish the payment on the client.`,
      }),
      values: {
        a: (str) => (
          <a href="/docs/api/payment_intents" target="_blank">
            {str}
          </a>
        ),
      },
    },
    response: samplePaymentIntent,
  },
  {
    id: 'load-stripe',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.ios.objc.steps.load-stripe.heading',
        defaultMessage: 'Install the SDK',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.ios.objc.steps.load-stripe.body',
        defaultMessage:
          'The iOS SDK is <a>open source</a>, fully documented, and compatible with apps supporting iOS 11 or above. Import Stripe in your project after installing.',
      }),
      values: {
        a: (str) => (
          <a href="http://github.com/stripe/stripe-ios" target="_blank">
            {str}
          </a>
        ),
      },
    },
    Component() {
      return <BuildInstructions instructions={buildInstructions} />;
    },
  },
  {
    id: 'publishable-key',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.ios.objc.steps.publishable-key.heading',
        defaultMessage: 'Set up the SDK',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.ios.objc.steps.publishable-key.body',
        defaultMessage:
          'Configure the Stripe SDK with your Stripe publishable API key.',
      }),
    },
  },
  {
    id: 'define-form',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.ios.objc.steps.define-form.heading',
        defaultMessage: 'Define your payment form',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.ios.objc.steps.define-form.body',
        defaultMessage:
          'Use Stripe’s <code>STPPaymentCardTextField</code> class to display a text field to securely collect card details. By using <code>STPPaymentCardTextField</code>, you guarantee that sensitive card details never touch your server.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    id: 'fetch-pi',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.ios.objc.steps.fetch-pi.heading',
        defaultMessage: 'Fetch a PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.ios.objc.steps.fetch-pi.body',
        defaultMessage:
          "Make a request to your server for a PaymentIntent as soon as the view loads. Store a reference to the PaymentIntent's client secret returned by the server.",
      }),
    },
  },
  {
    id: 'collect-details',
    section: 'complete-the-payment',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.ios.objc.steps.collect-details.heading',
        defaultMessage: 'Collect payment details',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.ios.objc.steps.collect-details.body',
        defaultMessage:
          'When the customer clicks the pay button, create an <a>STPPaymentIntentParams</a> object with the card details and client secret from the PaymentIntent you created on the server.',
      }),
      values: {
        a: (str) => (
          <a
            href="https://stripe.dev/stripe-ios/docs/Classes/STPPaymentIntentParams.html"
            target="_blank"
          >
            {str}
          </a>
        ),
      },
    },
  },
  {
    id: 'finish',
    section: 'complete-the-payment',
    heading: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.ios.objc.steps.finish.heading',
        defaultMessage: 'Finish the payment',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.ios.objc.steps.finish.body',
        defaultMessage:
          'Send the payment details to Stripe by calling <code>confirmPayment()</code>.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    id: 'handle-response',
    section: 'complete-the-payment',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.ios.objc.steps.handle-response.heading',
        defaultMessage: 'Handle the response from Stripe',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.ios.objc.steps.handle-response.body',
        defaultMessage:
          'If your customer’s card is declined, the completion block is called with an error. Show that error to your customer so they can try again with a different card. If no error occurred, tell your customer the payment was successful!',
      }),
    },
  },
  {
    id: 'test-cards',
    section: 'test-the-integration',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.ios.objc.steps.test-cards.heading',
        defaultMessage: 'Make a test payment',
      }),
    },
    testing: true,
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.ios.objc.steps.test-cards.body',
        defaultMessage:
          'Use a test card number to try your integration. These card numbers work in test mode with any CVC, postal code, and future expiry date. Stripe also has a set of <a>international test cards</a> to test specific postal code formats (e.g. only allow numerical values for U.S. zip codes).',
      }),
      values: {
        a: (str) => (
          <a href="/docs/testing#international-cards" target="_blank">
            {str}
          </a>
        ),
      },
    },
    Component: DefaultCards,
  },
]: StepsConfig);
